import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useT } from "@/glossary";
import { cn } from "@/lib/cn";
import { resetAllStorage } from "@/lib/stores/reset-all";
import {
  RESERVED_USERNAMES,
  saveAudioAuthorization,
  saveSuccessSoundEnabled,
  saveUsername,
  useUserPrefs,
} from "@/lib/stores/user-prefs";

export const Route = createFileRoute("/settings")({
  component: SettingsPage,
});

function SettingsPage() {
  const t = useT();
  const prefs = useUserPrefs();
  const [username, setUsername] = useState(prefs?.username ?? "");
  const [audioAuthorization, setAudioAuthorization] = useState(
    prefs?.audioAuthorization ?? true,
  );
  const [successSoundEnabled, setSuccessSoundEnabled] = useState(
    prefs?.successSoundEnabled ?? true,
  );

  if (!prefs) return null;

  const savedAudioAuthorization = prefs.audioAuthorization ?? true;
  const savedSuccessSoundEnabled = prefs.successSoundEnabled;

  const isReserved = RESERVED_USERNAMES.includes(username.trim());
  const hasChanges =
    username.trim() !== prefs.username ||
    audioAuthorization !== savedAudioAuthorization ||
    successSoundEnabled !== savedSuccessSoundEnabled;
  const canSave = username.trim().length > 0 && hasChanges && !isReserved;

  const handleSave = () => {
    if (!canSave) return;
    saveUsername(username.trim());
    saveAudioAuthorization(audioAuthorization);
    saveSuccessSoundEnabled(successSoundEnabled);
  };

  return (
    <div className="flex flex-col gap-8 p-4">
      <h1 className="font-bold text-xl">{t.settings.title}</h1>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <label htmlFor="username" className="text-muted-foreground">
            {t.onboarding.yourName}
          </label>
          <input
            id="username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            maxLength={50}
            className="rounded border border-foreground bg-background px-4 py-2"
          />
          <p
            className={cn(
              "text-destructive text-sm",
              !isReserved && "invisible",
            )}
          >
            {t.onboarding.nameReserved}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Switch
            id="audioAuth"
            labelId="audioAuth-label"
            checked={audioAuthorization}
            onCheckedChange={setAudioAuthorization}
          />
          <label
            id="audioAuth-label"
            htmlFor="audioAuth"
            className="text-muted-foreground"
          >
            {t.onboarding.allowRecordings}
          </label>
        </div>

        <div className="flex items-center gap-3">
          <Switch
            id="successSound"
            labelId="successSound-label"
            checked={successSoundEnabled}
            onCheckedChange={setSuccessSoundEnabled}
          />
          <label
            id="successSound-label"
            htmlFor="successSound"
            className="text-muted-foreground"
          >
            {t.settings.successSound}
          </label>
        </div>

        <Button onClick={handleSave} disabled={!canSave}>
          {t.common.save}
        </Button>
      </div>

      <div className="mt-8 border-foreground border-t pt-8">
        <p className="mb-4 text-muted-foreground text-sm">
          {t.settings.resetDescription}
        </p>
        <Button variant="danger" onClick={resetAllStorage}>
          {t.settings.resetButton}
        </Button>
      </div>
    </div>
  );
}
